import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "all");

  const categories = [
    { id: "all", name: "All Crystals", icon: "✦" },
    { id: "healing", name: "Healing Crystals", icon: "✧" },
    { id: "raw", name: "Raw Stones", icon: "◆" },
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const categoryParam = activeCategory !== "all" ? `?category=${activeCategory}` : "";
        const response = await axios.get(`${API}/products${categoryParam}`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [activeCategory]);

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    if (categoryId === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", categoryId);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="min-h-screen bg-mystic-void pt-24" data-testid="products-page">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-mystic-purple/30 to-mystic-void">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-mystic-gold" />
              <p className="text-mystic-gold font-medium tracking-widest uppercase text-sm">
                Sacred Collection
              </p>
              <Sparkles className="w-4 h-4 text-mystic-gold" />
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-mystic-silver mb-6 italic text-glow">
              {activeCategory === "all" 
                ? "All Crystals" 
                : activeCategory === "healing" 
                  ? "✧ Healing Crystals" 
                  : "◆ Raw Stones"}
            </h1>
            <p className="text-mystic-mist text-lg max-w-2xl">
              {activeCategory === "healing" 
                ? "Polished and charged crystals perfect for energy work, meditation, and spiritual awakening."
                : activeCategory === "raw"
                  ? "Unpolished natural specimens holding raw, primal earth energy."
                  : "Explore our curated collection of mystical crystals and ancient stones."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {/* Category Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-10" data-testid="category-filters">
            <Filter className="w-5 h-5 text-mystic-amethyst" />
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={activeCategory === category.id ? "default" : "outline"}
                onClick={() => handleCategoryChange(category.id)}
                className={`rounded-full ${
                  activeCategory === category.id
                    ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0 neon-glow"
                    : "border-white/20 text-mystic-mist hover:bg-white/10 hover:text-mystic-silver hover:border-mystic-amethyst/50"
                }`}
                data-testid={`filter-${category.id}`}
              >
                {category.icon} {category.name}
              </Button>
            ))}
            {activeCategory !== "all" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCategoryChange("all")}
                className="text-mystic-mist hover:text-mystic-orchid"
                data-testid="clear-filter"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Results count */}
          <div className="mb-8">
            <p className="text-mystic-mist text-sm">
              Showing <span className="font-medium text-mystic-amethyst">{products.length}</span> mystical items
            </p>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-square bg-white/5 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <Sparkles className="w-16 h-16 text-mystic-amethyst/30 mx-auto mb-4" />
              <p className="font-serif text-2xl text-mystic-silver mb-2 italic">No crystals found</p>
              <p className="text-mystic-mist">Try changing the filter or check back later.</p>
            </div>
          ) : (
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              data-testid="products-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-gradient-to-r from-mystic-purple/50 to-mystic-deep mt-12 border-t border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-serif text-2xl text-mystic-silver mb-2 italic">
                Can't find what you're seeking?
              </h3>
              <p className="text-mystic-mist/80">
                Reach out on WhatsApp for personalized crystal recommendations.
              </p>
            </div>
            <a
              href="https://wa.me/919030792052?text=🔮%20Hi!%20I'm%20looking%20for%20a%20specific%20crystal.%20Can%20you%20help?"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-help-btn"
            >
              <Button className="bg-whatsapp hover:bg-whatsapp/90 text-white rounded-full px-8 py-6 text-base font-medium whatsapp-pulse">
                Chat with Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
